import type { ReactNode } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import { useDatasetConfig, useDatasetAtoms } from '../dataset-context';
import { wellsResultsViewAtom } from '../atoms';
import type { MapQueryExport } from '../use-map-query-export';
import { WorkspacesResults } from './workspaces-results';
import { ResultsCards } from './results-cards';
import { ResultsTable } from './results-table';
import { useDatasetSearchChange } from './hooks';
import { WELLS_DATASET_ID } from './utils';

export { PREVIEW_HOST_ID } from './utils';

/**
 * A dataset's results: the runsheet cards (with preview rail and export) or the
 * flat table.
 * @param {object} props The section.
 * @param {MapQueryExport} props.mapQueryExport The page's CSV export.
 * @param {ReactNode} [props.toolbarStart] Leads the card list's toolbar.
 * @return {JSX.Element} The section.
 */
export function ResultsSection({
  mapQueryExport,
  toolbarStart,
}: {
  mapQueryExport: MapQueryExport;
  toolbarStart?: ReactNode;
}) {
  const config = useDatasetConfig();
  const atoms = useDatasetAtoms();
  const datasetText = useAtomValue(atoms.datasetTextAtom);
  const handleSearchChange = useDatasetSearchChange();
  // Wells or Workspaces, per mock v11's View dropdown.
  const [resultsView, setResultsView] = useAtom(wellsResultsViewAtom);

  if (config.resultsLayout === 'cards') {
    return (
      <ResultsCards
        mapQueryExport={mapQueryExport}
        toolbarStart={toolbarStart}
      />
    );
  }

  if (config.id === WELLS_DATASET_ID && resultsView === 'workspaces') {
    return (
      <WorkspacesResults
        view={resultsView}
        onViewChange={setResultsView}
        text={datasetText}
        onTextChange={handleSearchChange}
      />
    );
  }

  return <ResultsTable mapQueryExport={mapQueryExport} />;
}
